import i18n from '@/i18n';
import store from '../store';

// TODO: This function and updateQuantityExceedItemsLimit has different logic for k12 locations.
// Should discuss at some point what the logic should be for k12 locations for the max items
// per cart limit.
export function addToCartExceedLimit(): boolean {
	const isK12Location: boolean = store.getters['restaurant/isK12Location'];
	const maxItemsPerCart: number = store.getters['restaurant/getMaxItemsPerCart'];
	const currentItem: OrderItem = store.getters['cart/getTemporarySelectedItem'];
	let numberOfItems: number = currentItem.quantity;

	if(maxItemsPerCart) {
		if (isK12Location) {
			const orders = store.getters['cart/getRotationalMenuOrders'];
			orders.forEach((order: RotationalMenuOrder) => {
				order.items.forEach((item: OrderItem) => {
					numberOfItems += item.quantity;
				});
			});
		}
		else {
			const items: OrderItem[] = store.getters['cart/getItems'];
			items.forEach((item: OrderItem) => {
				numberOfItems += item.quantity;
			});
		}

		return numberOfItems > maxItemsPerCart;
	}
	else {
		return false;
	}
}

export function updateQuantityExceedItemsLimit(payload: { id: number, quantity: string }): boolean {
	const maxItemsPerCart: number = store.getters['restaurant/getMaxItemsPerCart'];
	const items: OrderItem[] = store.getters['cart/getItems'];
	let numberOfItems: number = +payload.quantity;

	if(maxItemsPerCart) {
		items.forEach((item: OrderItem) => {
			if(item.orderId !== payload.id) {
				numberOfItems += +item.quantity;
			}
		});

		return numberOfItems > maxItemsPerCart;
	}
	else {
		return false;
	}
}

/**
 * Check if adding an item to cart would exceed the maximum meals per cart limit
 * For K12 locations: checks each rotational menu order (day) separately
 * For non-K12 locations: checks total meals across all cart items
 * * Note: marketplace may need some tweaking to support this logic.
 *
 * @param {string} [orderDate] - The date for the order (required for K12 locations)
 * @return {boolean}
 */
export function addToCartExceedMealsLimit(orderDate?: string): boolean {
	const isK12Location: boolean = store.getters['restaurant/isK12Location'];
	const maxMealsPerCart: number = store.getters['restaurant/getMaxMealsPerCart'];
	const currentItem: OrderItem = store.getters['cart/getTemporarySelectedItem'];

	if (!maxMealsPerCart || !currentItem.meal_package) {
		return false;
	}

	// K12 ordering
	if (isK12Location) {
		const orders = store.getters['cart/getRotationalMenuOrders'];
		const targetOrder = orderDate ? orders.find((order: RotationalMenuOrder) => order.date === orderDate) : orders[0];
		let mealsForDay = currentItem.quantity;

		if (targetOrder) {
			mealsForDay += targetOrder.items.reduce((total: number, item: OrderItem) => {
				return item.meal_package ? total + item.quantity : total;
			}, 0);
		}

		return mealsForDay > maxMealsPerCart;
	}

	// Normal ordering
	const cartItems: OrderItem[] = store.getters['cart/getItems'];
	const totalMeals = cartItems.reduce((total: number, item: OrderItem) => {
		return item.meal_package ? total + item.quantity : total;
	}, currentItem.quantity);

	return totalMeals > maxMealsPerCart;
}

/**
 * Check if updating an item's quantity would exceed the maximum meals per cart limit
 * For K12 locations: checks each rotational menu order (day) separately
 * For non-K12 locations: checks total meals across all cart items
 * * Note: marketplace may need some tweaking to support this logic.
 *
 * @param {Object} payload
 * @param {number} payload.id
 * @param {string} payload.quantity
 * @return {boolean}
 */
export function updateQuantityExceedMealsLimit(payload: { id: number, quantity: string }): boolean {
	const isK12Location: boolean = store.getters['restaurant/isK12Location'];
	const maxMealsPerCart: number = store.getters['restaurant/getMaxMealsPerCart'];

	if (!maxMealsPerCart) {
		return false;
	}

	// K12
	if (isK12Location) {
		const orders = store.getters['cart/getRotationalMenuOrders'];
		const targetOrder = orders.find((order: RotationalMenuOrder) =>
			order.items.some((item: OrderItem) => item.orderId === payload.id)
		);

		if (!targetOrder) {
			return false;
		}

		const mealsForDay = targetOrder.items.reduce((total: number, item: OrderItem) => {
			if (!item.meal_package) {
				return total;
			}
			const quantity = item.orderId === payload.id ? +payload.quantity : item.quantity;
			return total + quantity;
		}, 0);

		return mealsForDay > maxMealsPerCart;
	}

	// Normal ordering
	const cartItems: OrderItem[] = store.getters['cart/getItems'];
	const totalMeals = cartItems.reduce((total: number, item: OrderItem) => {
		if (!item.meal_package) {
			return total;
		}
		const quantity = item.orderId === payload.id ? +payload.quantity : item.quantity;
		return total + quantity;
	}, 0);

	return totalMeals > maxMealsPerCart;
}

/**
 * Detect which card type it is with regex
 *
 * @param {string} cardNumber
 * @return {string}
 */
export function findCardType(cardNumber: string): string {
	const amex_regex = new RegExp('^3[47][0-9]{0,}$'); // American Express: 34, 37
	const visa_regex = new RegExp('^4[0-9]{0,}$'); // Visa: 4
	const mc_regex = new RegExp('^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)[0-9]{0,}$'); // Mastercard: 2221-2720, 51-55
	if(cardNumber.match(amex_regex)) {
		return 'AMEX';
	}
	if(cardNumber.match(mc_regex)) {
		return 'MC';
	}
	if(cardNumber.match(visa_regex)) {
		return 'VISA';
	}
	return '';
}

/**
 * Get the dynamic field value depending on the locale
 * We start by checking if the element contains a localization object.
 * If it does, then we check if there is a localization object for the
 * current locale selected. If not we default to the default value
 * of the element.
 *
 * @param {LocaleElement} element
 * @param {Localization | undefined} localization
 * @param {string} field
 * @param {RestaurantLocale[] | undefined} locales
 * @return {string}
 */
export function getDynamicFieldValue(element: LocaleElement, localization: Localization | undefined, field: string, locales: RestaurantLocale[] | undefined): string {
	const locale = locales && locales.find((locale: RestaurantLocale) => {
		return locale.locale_short === i18n.locale.substring(0,2);
	})!;
	if(locale && localization && i18n?.locale && localization[i18n.locale.substring(0,2)] && localization[i18n.locale.substring(0,2)][field]) {
		return localization[i18n.locale.substring(0,2)][field];
	}
	return element[field];
}


/**
 * Scroll to the element if it is out of the view
 *
 * @param {Element} element
 * @param {ScrollIntoViewOptions} scrollOptions
 * @return {void}
 */
export function scrollIntoViewIfNeeded(element: Element, scrollOptions: ScrollIntoViewOptions): void {
	if (element.getBoundingClientRect().bottom > window.innerHeight || element.getBoundingClientRect().top < 0) {
		element.scrollIntoView(scrollOptions);
	}
}

/**
 * Get the Y positions on the screen of the menus or sections
 *
 * @param {Menu[] | MenuSection[]} elements
 * @return {number[]}
 */
export function getYPositionsOfElements(elements: Menu[] | MenuSection[]): number[] {
	const yPositions: number[] = [];

	elements.forEach((element, sIdx) => {
		const docElement = document.getElementById(`main-divider-${element.slug}-${sIdx}`);
		if (docElement) {
			yPositions.push(docElement.getBoundingClientRect().top - 50);
		}
		else {
			yPositions.push(-1000);
		}
	});

	return yPositions;
}

/**
 * Get the selected menu or section based on scroll position
 *
 * @param {number[]} yPositions
 * @param {number} windowHeight
 * @return {number} index of the selected element in view
 */
export function getSelectionFromVisibleSections(yPositions: number[], windowHeight: number): number {
	const pageHalfwayPoint = windowHeight / 2;

	if (yPositions[0] > pageHalfwayPoint) {
		return 0;
	}

	for (let i = 0; i < yPositions.length; i++) {
		if (i + 1 === yPositions.length || (yPositions[i] < pageHalfwayPoint && yPositions[i + 1] > pageHalfwayPoint)) {
			return i;
		}
	}
	return 0;
}

/**
	 * Scroll to the respective menu or section selected
	 *
	 * @param {MenuSection} section
	 * @param {number} sIdx
	 * @param {number} topOffset
	 * @return {void}
	 */
export function scrollToElement(section: MenuSection, sIdx: number, topOffset: number): void {
	const sectionHeaderElement = document.getElementById(`main-divider-${section.slug}-${sIdx}`);
	const navBar = document.getElementById('menu-nav-container');

	if (section && sectionHeaderElement && navBar) {
		const navBarStyle = window.getComputedStyle(navBar);
		let navBarHeight = navBar.offsetHeight;
		if (navBarStyle['marginTop']) {
			navBarHeight += parseFloat(navBarStyle['marginTop']);
		}
		if (navBarStyle['marginBottom']) {
			navBarHeight += parseFloat(navBarStyle['marginBottom']);
		}
		if (navBarStyle['paddingTop']) {
			navBarHeight += parseFloat(navBarStyle['paddingTop']);
		}
		if (navBarStyle['paddingBottom']) {
			navBarHeight += parseFloat(navBarStyle['paddingBottom']);
		}
		window.scrollTo({ top: window.pageYOffset + sectionHeaderElement.getBoundingClientRect().top - navBarHeight - topOffset, behavior: 'smooth' });
	}
}

/**
 * Go through the menu item and its nested options to get the allergens/diets filters used by the restaurant
 *
 * @param {string[]} currentFilters
 * @param {string} key
 * @param {MenuItem | Option} item
 * @returns  {string[]} - List of the diets/allergens
 */
export function generateAvailableFilters(currentFilters: string[], key: 'diets'|'allergens', item: MenuItem|Option): string[] {
	let mutableCurrentFilters: string[] = [...currentFilters];
	item[key] && item[key]?.split(',').forEach((diet: string) => {
		if (!mutableCurrentFilters.includes(diet)) {
			mutableCurrentFilters.push(diet);
		}

		if (item.options?.length) {
			item.options.forEach((option: OptionGroup) => {
				if (option.values && option.values.length) {
					option.values.forEach((value: Option) => {
						mutableCurrentFilters = generateAvailableFilters(mutableCurrentFilters, key, value);
					});
				}
			});
		}
	});
	return mutableCurrentFilters;
}

/**
 * Check if the sku or id of the available element matches the sku.
 * Check the id if no sku.
 *
 * @param {MenuItem | Option} element - Can be a menu item, option or sub-option
 * @param {number} [id]
 * @param {string|null} [sku] - If it starts with 'id-' then it is an id, otherwise it is a sku
 * @return {boolean}
 */
export function getElementBySkuOrId(element: MenuItem | Option, id?: number, sku?: string|null): boolean {
	if (element.unavailable) {
		return false;
	}

	// If an item has no sku in our db, the sku will be id-<id>
	const idFromSku = sku && sku.startsWith('id-') ? +sku.split('id-')[1] : null;

	// Search by sku
	if (sku && element.sku) {
		return idFromSku ? element.id === idFromSku : element.sku === sku;
	}

	// Search by ID
	return element.id === (idFromSku || id);
}


/**
 * This function makes sure that the user's student's data is aligned with the manager's allowed school delivery values
 * It covers: schools, grades, classrooms, teachers
 *
 * @param {UserStudent | Partial<UserStudent>} params.student - The student object to validate
 * @param {SchoolDelivery} params.restaurantDeliveryInfo - The restaurant delivery information containing schools and their details
 * @param {SchoolDeliveryOptions} params.schoolDeliveryOptions - The school delivery options from the restaurant
 * @return {void}
 */
export function validateStudentFields({ student, restaurantDeliveryInfo, schoolDeliveryOptions }: { student: UserStudent | Partial<UserStudent>; restaurantDeliveryInfo: SchoolDelivery; schoolDeliveryOptions: SchoolDeliveryOptions; }): void {
	// If there is only one school, return the fields for that school
	// If there are multiple schools, return the fields for the selected school
	const selectedSchool = restaurantDeliveryInfo.schools.length === 1
		? restaurantDeliveryInfo.schools[0]
		: student.school
			? restaurantDeliveryInfo.schools.find(school => school.name === student.school)
			: null;

	if (!selectedSchool) {
		student.school = '';
		student.grade = '';
		student.classroom = '';
		student.teacher = '';
		return;
	}

	const validGrades = selectedSchool.grades?.split(',') ?? null;
	if (!selectedSchool.grades || (validGrades && !validGrades.includes(student.grade ?? ''))) {
		student.grade = '';
	}

	const validClassrooms = selectedSchool.classrooms?.split(',') ?? null;
	if (!selectedSchool.classrooms || (validClassrooms && !validClassrooms.includes(student.classroom ?? ''))) {
		student.classroom = '';
	}

	// Case 1 (if): checks if the teacher selected is part of the allowed school teachers
	// Case 2 (1st else-if): checks if the teachers are enabled on Gusteau
	// Case 3 (2nd else-if): if the above conditions are not met, it means that the teacher is allowed, and is a free form text.
	if (Array.isArray(selectedSchool.teachers?.split(',')) && selectedSchool.teachers && selectedSchool.teachers.length > 0) {
		const validTeachers = selectedSchool.teachers.split(',') as string[];
		if (student.teacher && !validTeachers.includes(student.teacher)) {
			student.teacher = '';
		}
	}
	else if (schoolDeliveryOptions.teachers && !(schoolDeliveryOptions.teachers?.length >= 0)) {
		student.teacher = '';
	}
	else if (!schoolDeliveryOptions.teachers) {
		student.teacher = '';
	}
}

/**
 * This function populates the school's related fields.
 * It returns the school's grades, classrooms and techers of the selected school.
 * If there is only one school configured, it returns that school's grades, classrooms and teachers by default.
 *
 * @param {SchoolDelivery} params.restaurantDeliveryInfo - The restaurant delivery information containing schools and their details
 * @param {string} params.selectedSchoolName - The selected school's name
 * @return {SchoolDeliveryOptions}
 */
export function getSchoolDeliveryOptions(restaurantDeliveryInfo: SchoolDelivery, selectedSchoolName?: string): SchoolDeliveryOptions {
	const schools = restaurantDeliveryInfo.schools;

	const selectedSchool = schools.length === 1
		// If there is only one school, return the fields for that school
		? schools[0]
		// If there are multiple schools, return the fields for the selected school
		: selectedSchoolName
			? schools.find(s => s.name === selectedSchoolName)
			: null;

	if (!selectedSchool) {
		return {
			grades: schools.some(s => s.grades) ? [] : null,
			classrooms: schools.some(s => s.classrooms) ? [] : null,
			teachers: schools.some(s => s.teachers !== undefined) ? [] : null
		};
	}

	return {
		grades: selectedSchool.grades?.split(',') ?? null,
		classrooms: selectedSchool.classrooms?.split(',') ?? null,
		teachers: selectedSchool.teachers !== undefined
			? selectedSchool.teachers.length
				? selectedSchool.teachers.split(',')
				: []
			: null
	};
}

/**
 * Logout user from Facebook if logged in
 *
 * @return {void}
 */
export function logOutFromFacebook(): void {
	FB?.getLoginStatus((response: fb.StatusResponse) => {
		if (response.status === 'connected') {
			FB.logout();
		}
	});
}